import { formatDurationClock, formatDurationHeroMeta } from '../lib/duration-format';
import { extractYoutubeId, youtubeIframeEmbedSrc } from '../lib/media-url';
import { ensureYoutubeIframeApi } from '../lib/yt-iframe-api';

interface YtPlayerInstance {
  getDuration: () => number;
  destroy: () => void;
}

type YtPlayerCtor = new (
  el: HTMLElement,
  opts: {
    videoId?: string;
    width?: number;
    height?: number;
    playerVars?: Record<string, string | number>;
    events?: {
      onReady?: (e: { target: YtPlayerInstance }) => void;
      onError?: () => void;
    };
  },
) => YtPlayerInstance;

function getYt(): { Player: YtPlayerCtor } | undefined {
  return (window as unknown as { YT?: { Player: YtPlayerCtor } }).YT;
}

async function fetchYoutubeDurationSeconds(videoId: string): Promise<number | null> {
  await ensureYoutubeIframeApi();
  const YT = getYt();
  if (!YT?.Player) {
    return null;
  }
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('title', '');
    iframe.style.cssText =
      'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;border:0';

    let settled = false;
    let player: YtPlayerInstance | null = null;

    const done = (d: number | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      try {
        player?.destroy();
      } catch {
        /* noop */
      }
      try {
        iframe.remove();
      } catch {
        /* noop */
      }
      resolve(d);
    };

    const timeoutId = window.setTimeout(() => done(null), 20000);

    iframe.addEventListener(
      'load',
      () => {
        if (settled) {
          return;
        }
        try {
          player = new YT.Player(iframe, {
            events: {
              onError: (): void => done(null),
              onReady: (e: { target: YtPlayerInstance }): void => {
                const read = (): number => e.target.getDuration();
                const finish = (d: number): void => {
                  done(Number.isFinite(d) && d > 0 ? d : null);
                };
                const d0 = read();
                if (d0 > 0) {
                  finish(d0);
                  return;
                }
                window.setTimeout(() => finish(read()), 500);
              },
            },
          });
        } catch {
          done(null);
        }
      },
      { once: true },
    );

    iframe.src = youtubeIframeEmbedSrc(videoId);
    document.body.appendChild(iframe);
  });
}

function probeFileDurationSeconds(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const cleanup = (): void => {
      v.removeAttribute('src');
      v.load();
    };
    const to = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, 25000);
    v.onloadedmetadata = (): void => {
      window.clearTimeout(to);
      const d = v.duration;
      cleanup();
      resolve(Number.isFinite(d) && d > 0 ? d : null);
    };
    v.onerror = (): void => {
      window.clearTimeout(to);
      cleanup();
      resolve(null);
    };
    v.src = url;
  });
}

function applyResolvedDuration(showcase: HTMLElement, pick: HTMLElement, seconds: number): void {
  const label = formatDurationClock(seconds);
  if (!label) {
    return;
  }
  pick.dataset.fcShowcaseDuration = label;
  pick.removeAttribute('data-fc-showcase-duration-probe');

  const thumb = pick.querySelector<HTMLElement>('[data-fc-showcase-thumb-duration]');
  if (thumb) {
    thumb.textContent = label;
    thumb.hidden = false;
  }

  const isCurrent = pick.getAttribute('aria-current') === 'true';
  if (isCurrent) {
    const overlayDur = showcase.querySelector<HTMLElement>('[data-fc-showcase-overlay-duration]');
    if (overlayDur) {
      overlayDur.textContent = formatDurationHeroMeta(label);
      overlayDur.hidden = false;
      overlayDur.removeAttribute('data-fc-showcase-overlay-duration-pending');
    }
  }
}

async function resolveProbeForPick(showcase: HTMLElement, pick: HTMLElement): Promise<void> {
  const kind = pick.dataset.fcShowcaseDurationProbe?.trim();
  const url = pick.dataset.fcShowcaseUrl?.trim();
  if (!kind || !url) {
    return;
  }

  let sec: number | null = null;
  if (kind === 'file') {
    sec = await probeFileDurationSeconds(url);
  } else if (kind === 'youtube') {
    const id = extractYoutubeId(url);
    if (id) {
      sec = await fetchYoutubeDurationSeconds(id);
    }
  }

  if (sec != null) {
    applyResolvedDuration(showcase, pick, sec);
  }
}

/**
 * Uzupełnia czas trwania: pliki (metadata / nagłówek wideo) oraz YouTube (IFrame API).
 * Ręczne pole „Czas trwania” w dialogu ma pierwszeństwo (brak data-fc-showcase-duration-probe).
 */
export function initVideoShowcaseDurations(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-fc-showcase]').forEach((showcase) => {
    const picks = [
      ...showcase.querySelectorAll<HTMLElement>(
        '[data-fc-showcase-item][data-fc-showcase-duration-probe]',
      ),
    ];
    if (picks.length === 0) {
      return;
    }
    void Promise.all(picks.map((pick) => resolveProbeForPick(showcase, pick)));
  });
}
